import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';

import { useParams, useQuery } from '../../../contexts/RouterContext';
import { useEmailReport } from '../../../hooks/api/reports/useEmailReport';
import { usePublishReport } from '../../../hooks/api/reports/usePublishReport';
import { useQueuePublicAttributionReport } from '../../../hooks/api/reports/useQueuePublicAttributionReport';
import { useUnpublishReport } from '../../../hooks/api/reports/useUnpublishReport';
import useMultipleModals from '../../../hooks/useMultipleModals';
import { nuc, openInNewTab } from '../../../utils';
import Button from '../../generic/Button';
import Icon, { icons, IconText } from '../../generic/Icon';
import { Menu, MenuDivider, MenuItem } from '../../generic/Menu';
import useReportUrl from '../../shared/publicPortal/hooks/useReportUrl';
import { buildPublicReportURL } from '../hooks/buildPublicReportURL';
import { useCanPublishLicensingTxt } from '../hooks/useCanPublishLicensingTxt';
import { useCanPublishSbom } from '../hooks/useCanPublishSbom';
import { useDownloadReport } from '../hooks/useDownloadReport';
import { useReportOptionsParams } from '../hooks/useReportOptionsParams';
import { useReportType } from '../hooks/useReportType';
import { useSbomPublishState } from '../hooks/useSbomPublishState';
import { isReleaseGroupScope, type ReportsV3Params, type ReportsV3QueryParams, type ReportV3Format } from '../types';

import EmailReportDialog from './EmailReportDialog';
import PublishReportDialog from './PublishReportDialog';
import { UnpublishReportDialog } from './UnpublishReportDialog';

import styles from './GenerateReportButton.module.less';

export type GenerateReportAction = 'generateLink' | 'download' | 'email' | 'publish';

type Props = {
  /**
   * Whether the report is currently being generated.
   */
  isLoading: boolean;
  /**
   * Whether to show the "Generate Public Link" option.
   */
  shouldShowPublicLink: boolean;
 /**
  * Whether to show the "Email" option.
  */
  shouldShowEmail: boolean;
  /**
   * Whether to show the "Publish to Portal" option (SBOM reports, release group scope).
   */
  shouldShowPublish?: boolean;
  /**
   * Whether the report is already published to the portal (toggles Publish/Republish wording).
   */
  isPublished?: boolean;
  /**
   * Whether a publish is currently in progress (shown as the publish item's status line).
   */
  isPending?: boolean;
  /**
   * Human-readable time the SBOM was last published, shown in the publish item's
   * status line once published.
   */
  lastPublishedAt?: string | null;
  /**
   * Called when the user clicks "Unpublish" (opens the confirmation dialog).
   */
  onUnpublishClick?: () => void;
  /**
   * Callback to be called when an item is clicked.
   */
  onItemClick: (action: GenerateReportAction) => void;
};

/**
 * "View Report" menu item, rendered only once an SBOM is published. Mounting it
 * behind the published check keeps the release-scoped `useReportUrl` safe to
 * call; it renders nothing unless the org portal is public and a URL exists.
 */
const ViewReportMenuItem: VoidFunctionComponent = () => {
  const { url, isPublic } = useReportUrl();
  if (!isPublic || !url) {
    return null;
  }
  return (
    <MenuItem className={styles.menuItem} onClick={() => openInNewTab(url)}>
      <Icon icon={icons.externalLink} /> View Report
    </MenuItem>
  );
};

/**
 * Button + dropdown menu which allows the user to generate the report.
 * The dropdown items will eventually change based on the report type and format.
 */
export const GenerateReportButton: VoidFunctionComponent<Props> = ({
  isLoading,
  shouldShowPublicLink,
  shouldShowEmail,
  shouldShowPublish = false,
  isPublished = false,
  isPending = false,
  lastPublishedAt = null,
  onUnpublishClick,
  onItemClick,
}) => {
  // The public-link and publish options each add a second action, so either one
  // means we need a dropdown menu rather than a single flat button.
  const hasMenu = shouldShowPublicLink || shouldShowPublish;

  // Small status line shown beneath the "Publish to Portal" item.
  let publishStatusText = 'Not published';
  if (isPending) {
    publishStatusText = 'Publishing';
  } else if (isPublished) {
    publishStatusText = lastPublishedAt ? `Last published: ${lastPublishedAt}` : 'Published';
  }

  // When there's only one possible action, render a flat button.
  if (!hasMenu) {
    const action: GenerateReportAction = shouldShowEmail ? 'email' : 'download';
    const icon = shouldShowEmail ? icons.envelope : icons.download;
    const label = shouldShowEmail ? 'Send to Email' : 'Download Report';

    return (
      <Button
        type="primary"
        size="medium"
        onClick={() => onItemClick(action)}
        isFullWidth
        isDisabled={isLoading}
        icon={icon}
      >
        {label}
      </Button>
    );
  }

  return (
    <Menu
      menuButton={
        <Button type="primary" size="medium" onClick={_.noop} isFullWidth>
          <IconText icon={icons.chevronDown} text={shouldShowPublish ? 'Publish Report' : 'Generate Report'} alignIcon="right" />
        </Button>
    }
      onItemClick={(event) => onItemClick(event.value as GenerateReportAction)}
      direction="bottom"
      align="end"
  >
      {shouldShowPublicLink && (
        <MenuItem className={styles.menuItem} value="generateLink" disabled={isLoading}>
          <Icon icon={icons.link} /> {isLoading ? 'Generating...' : 'Generate Public Link'}
        </MenuItem>
      )}
      {shouldShowEmail ? (
        <MenuItem className={styles.menuItem} value="email">
          <Icon icon={icons.envelope} /> Send to Email
        </MenuItem>
      ) : (
        <MenuItem className={styles.menuItem} value="download">
          <Icon icon={icons.download} /> Download Report
        </MenuItem>
      )}
      {shouldShowPublish && (
        <MenuItem className={styles.menuItem} value="publish" disabled={isPending}>
          <Icon icon={icons.bookRegular} />
          <span className={styles.publishItem}>
            {isPublished ? 'Republish' : 'Publish to Portal'}
            <span className={styles.publishItemStatus}>{publishStatusText}</span>
          </span>
        </MenuItem>
      )}
      {shouldShowPublish && isPublished && <MenuDivider />}
      {shouldShowPublish && isPublished && <ViewReportMenuItem />}
      {shouldShowPublish && isPublished && (
        <MenuItem className={styles.menuItem} onClick={onUnpublishClick}>
          <Icon icon={icons.eyeSlash} /> Unpublish
        </MenuItem>
      )}
    </Menu>
  );
};

// We always email PDF reports, because generating + downloading them would be too slow.
// Note that the exception to this rule is with Remediation Guidance reports, because they are typically smaller and can be downloaded quickly.
const alwaysEmailFormat: ReportV3Format = 'PDF';

/**
 * Container component which manages the state for the GenerateReport button.
 */
export const GenerateReportButtonContainer = () => {
  const reportType = useReportType();
  const params = useParams<ReportsV3Params>();
  const isReleaseGroup = isReleaseGroupScope(params);
  const [query] = useQuery<ReportsV3QueryParams>();

  const reportFormat = query.format;
  const projectLocator =
    isReleaseGroup ? '' : nuc(`${params.project_id}$${params.revision_id}`);

  const reportOptionsParams = useReportOptionsParams();
  const { mutate: queuePublicReport, isPending: isGeneratingLink } = useQueuePublicAttributionReport();

  // This hook just neatly wraps up all the logic around the function which opens the download endpoint in a new tab.
  // In the browser this will start the download process immediately.
  const { downloadReport } = useDownloadReport(reportFormat as ReportV3Format);

  const { emailModal, publishModal, unpublishModal } = useMultipleModals('email', 'publish', 'unpublish');

  // Email report flow
  const { mutate: sendEmailReport, isPending: isEmailSending } = useEmailReport(reportFormat as ReportV3Format);

  // Publish to portal flow: SBOM reports, plus Licensing Plain Text (TXT)
  // reports, both at the release group level only.
  const canPublishSbom = useCanPublishSbom();
  const canPublishLicensingTxt = useCanPublishLicensingTxt();
  const canPublish = canPublishSbom || canPublishLicensingTxt;
  // Dialog titles and toasts say "Licensing Report" for a Licensing TXT publish,
  // and "SBOM" otherwise.
  const reportLabel = canPublishLicensingTxt ? 'Licensing Report' : 'SBOM';
  const { isPublished, isPending: isPublishPending, lastPublishedAt } = useSbomPublishState();
  const { mutateAsync: publishReport, isPending: isPublishing } = usePublishReport(
    reportFormat as ReportV3Format,
    reportLabel,
  );
  const { mutate: unpublishReport, isPending: isUnpublishing } = useUnpublishReport();

  // TODO: Implement logic for "if the project is too large, they can't download it".
  const isTooLargeToDownload = isReleaseGroup || false;
  const isDownloadingPDFsAllowed = reportType === 'remediationGuidance';
  const shouldShowEmail = isTooLargeToDownload || (reportFormat === alwaysEmailFormat && !isDownloadingPDFsAllowed);
  const shouldShowPublicLink = !isTooLargeToDownload && !isReleaseGroup && reportType !== 'remediationGuidance';

  const onItemClick = (action: GenerateReportAction) => {
    if (action === 'generateLink' && !isGeneratingLink) {
      queuePublicReport(buildPublicReportURL({
        projectLocator,
        format: reportFormat,
        reportOptionsParams,
      }));
    }
    if (action === 'download') {
      downloadReport();
    }
    if (action === 'email') {
      emailModal.onOpen();
    }
    if (action === 'publish' && !isPublishPending) {
      publishModal.onOpen();
    }
  };

  return (
    <>
      <GenerateReportButton
        isLoading={isGeneratingLink}
        shouldShowPublicLink={shouldShowPublicLink}
        shouldShowEmail={shouldShowEmail}
        shouldShowPublish={canPublish}
        isPublished={isPublished}
        isPending={isPublishPending}
        lastPublishedAt={lastPublishedAt}
        onUnpublishClick={unpublishModal.onOpen}
        onItemClick={onItemClick}
      />
      {emailModal.isOpen && (
        <EmailReportDialog
          onClose={emailModal.onClose}
          onConfirm={(emails) => {
            sendEmailReport(emails);
            emailModal.onClose();
          }}
          isLoading={isEmailSending}
        />
      )}
      {publishModal.isOpen && (
        <PublishReportDialog
          onClose={publishModal.onClose}
          onConfirm={async () => {
            /*
             * Keep the dialog open (button shows a loader) until the publish
             * request resolves: close on success, stay open on error so the
             * user can retry. The mutation already surfaces an error toast, so
             * there's nothing to handle in the catch.
             */
            try {
              await publishReport();
              publishModal.onClose();
            } catch {
              // Publish failed; error toast is shown by the mutation.
            }
          }}
          isLoading={isPublishing}
          isPublished={isPublished}
          reportLabel={reportLabel}
        />
      )}
      {unpublishModal.isOpen && (
        <UnpublishReportDialog
          onClose={unpublishModal.onClose}
          onConfirm={() => {
            unpublishReport();
            unpublishModal.onClose();
          }}
          isLoading={isUnpublishing}
          reportLabel={reportLabel}
        />
      )}
    </>
  );
};
